import React, { useContext, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import InputLabel from '@mui/material/InputLabel';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { OpenAPIV3_1 as OpenAPI } from "openapi-types";
import _ from 'lodash';
import { resolve, getResponseExample, getResponseModel } from '../utils';
import { DocContext } from '../Documentation';
import formatHighlight from 'json-format-highlight';

export interface ResponseBoxProps {
    responsesObject: OpenAPI.ResponsesObject | undefined;
}

const Header: React.FC<{
    name: string;
    headerObj: OpenAPI.HeaderObject;
}> = ({ name, headerObj }) => {
    return <Box>
        {name}: {headerObj.description}
    </Box>
}

const SingleResponse: React.FC<{
    httpCode: string;
    responseObject: OpenAPI.ResponseObject;

}> = ({ httpCode, responseObject }) => {

    const { description, content } = responseObject;
    const document: OpenAPI.Document = useContext(DocContext)!;
    const mediaTypes = _.keys(content);
    const [tabNum, setTabNum] = useState(0);
    const [mediaType, setMediaType] = useState<string | undefined>(mediaTypes.length > 0 ? mediaTypes[0] : undefined);
    const [mediaTypeObj, setMediaTypeObj] = useState<OpenAPI.MediaTypeObject | undefined>(mediaType ? content![mediaType] : undefined);
    const [responseSchema, setResponseSchema] = useState<OpenAPI.SchemaObject | undefined>(undefined);

    const handleChange = (event: SelectChangeEvent) => {
        const mediaTypeKey = event.target.value as string;
        const mediaTypeObj = content![mediaTypeKey];

        setMediaType(mediaTypeKey);
        setMediaTypeObj(mediaTypeObj);
    };

    const handleTabNum = (event: React.SyntheticEvent, newValue: number) => {
        setTabNum(newValue);
    };

    useEffect(
        () => {
            const schema = mediaTypeObj?.schema as any;
            if (schema !== undefined) {
                setResponseSchema(schema["$ref"] ? resolve(schema, document) : schema);
            }
        },
        [mediaTypeObj]
    );

    return <Box>
        <Box sx={{ display: 'flex' }}>
            <Typography sx={{ mr: 1, fontWeight: 700 }}>{httpCode}:</Typography>
            <Typography>{description}</Typography>
        </Box>
        {
            mediaType !== undefined && <Box sx={{
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center'
            }}>
                <Tabs sx={{ overflow: 'inherit' }} value={tabNum} onChange={handleTabNum}>
                    <Tab label="Example" />
                    <Tab label="Model" />
                </Tabs>
                {
                    mediaTypes.length > 0 && <Box sx={{ flex: 1, textAlign: 'right' }}>
                        <FormControl size='small'>
                            <Select
                                value={mediaType}
                                onChange={handleChange}
                            >
                                {
                                    mediaTypes.map(mediaType => (
                                        <MenuItem
                                            value={mediaType}
                                            key={mediaType}>
                                            {mediaType}
                                        </MenuItem>
                                    ))
                                }
                            </Select>
                        </FormControl>
                    </Box>
                }
            </Box>
        }
        {
            (tabNum === 0 && responseSchema !== undefined) && <Box sx={{ fontSize: '0.8em' }}>
                <pre dangerouslySetInnerHTML={{ __html: formatHighlight(getResponseExample(responseSchema, document)) }} />
            </Box>
        }
        {
            (tabNum === 1 && responseSchema !== undefined) && <Box sx={{ fontSize: '0.8em' }}>
                <pre dangerouslySetInnerHTML={{ __html: formatHighlight(getResponseModel(responseSchema, document)) }} />
            </Box>
        }
        {
            responseObject.headers !== undefined && <>
                <Typography>{'Response Headers'}</Typography>
                {
                    _.keys(responseObject.headers).map(header => (
                        <Header
                            key={header}
                            name={header}
                            headerObj={resolve(responseObject.headers![header], document)}
                        />
                    ))
                }
            </>
        }
    </Box>
}

const ResponseBox: React.FC<ResponseBoxProps> = ({ responsesObject }) => {
    const document: OpenAPI.Document = useContext(DocContext)!;
    const httpCodes = _.keys(responsesObject);

    return <Box>
        {
            httpCodes.map((httpCode, index) => {
                const response: OpenAPI.ResponseObject | OpenAPI.ReferenceObject = responsesObject![httpCode]!;
                const responseObj: OpenAPI.ResponseObject = resolve(response, document);

                return <Box key={httpCode} sx={{ mb: 1 }}>
                    <SingleResponse
                        key={httpCode}
                        httpCode={httpCode}
                        responseObject={responseObj}
                    />
                    {
                        index < (httpCodes.length - 1) && <Divider />
                    }
                </Box>
            })
        }
    </Box>
};

export default ResponseBox;
